import React from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import { Modal, Select, message } from "antd";
import { Hooks, Context, Cookie, Request } from "@xt/xtbase";
import { useRouter } from "next/router";
import AppCookie from "utils/cookie";
import LocalesMap from "locales/map";
import assistConfig from "@xt/Nav/src/config/assistConfig";
import useIsDesktop from "@/hooks/useIsDesktop";
import store from "@/store";
import { getUserNoticeConfigList, get_noticeLang, putUserNoticeConfig, put_noticeLang } from "@/api/xt-messgae";
import { axiosThrottle } from "@/utils/method";
import { waitRes } from "@/utils/axios";
import modalStyles from "../Index/securityModals.module.scss";
import styles from "./index.module.scss";

const { useTranslation } = Hooks;
const { XtContext } = Context;
const { setHeader } = Request;

const CHEVRON_RIGHT = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.5 2.5L7.5 6L4.5 9.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CURRENCY_OPTS = assistConfig.map((c: { key: string; name: string }) => ({ key: c.key, name: c.name }));

/** 通知语言选项 — 与 NotificationSetting 一致 */
const NOTICE_LANGS = [
  { label: "English", value: "en" },
  { label: "Português", value: "pt" },
  { label: "العربية", value: "ar" },
  { label: "Tiếng Việt", value: "vi" },
];

const localeToLangKey = (locale: string | undefined): string | undefined =>
  !locale || locale === "default" ? undefined : locale === "zh-CN" ? "cn" : locale === "zh-HK" ? "hk" : locale;

const getLangKeyFromPathname = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  const first = (window.location.pathname || "").split("/").filter(Boolean)[0];
  if (!first) return undefined;
  const found = LocalesMap.find((item: { key: string; name: string }) => item.key === first || item.name === first);
  return found ? (found as { key: string }).key : undefined;
};

function Segmented<T extends string>({
  options,
  value,
  onChange,
  labels,
}: {
  options: readonly T[];
  value: T;
  onChange: (v: T) => void;
  labels?: Record<string, string>;
}) {
  return (
    <div className={styles.segmented} role="group">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          className={cx(styles.segmentedBtn, value === o && styles.segmentedBtnActive)}
          onClick={() => onChange(o)}
        >
          {labels?.[o] ?? o}
        </button>
      ))}
    </div>
  );
}

function Toggle({ on, onChange }: { on: boolean; onChange?: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      className={cx(styles.toggle, on && styles.toggleOn)}
      onClick={() => onChange?.(!on)}
    >
      <span className={styles.toggleKnob} />
    </button>
  );
}

interface SettingProps extends BaseCompProps {
  attr?: any;
}

const Setting: React.FC<SettingProps> = ({ className }) => {
  const t = useTranslation();
  const router = useRouter();
  const { push, locale: routerLocale, replace } = router;
  const [appState, appDispatch] = React.useContext(XtContext);
  const { isDesktop, isReady } = useIsDesktop();
  const isSpikexPc = isReady && isDesktop;

  const [pathnameLang, setPathnameLang] = React.useState<string | undefined>(undefined);
  const [marketingOn, setMarketingOn] = React.useState(true);
  const [noticeLangVisible, setNoticeLangVisible] = React.useState(false);
  const [noticeLangVal, setNoticeLangVal] = React.useState("");
  const [noticeLangStr, setNoticeLangStr] = React.useState("");

  React.useEffect(() => {
    setPathnameLang(getLangKeyFromPathname());
  }, []);

  const loadNoticeLang = React.useCallback(async () => {
    try {
      const res = await get_noticeLang();
      setNoticeLangVal(res.lang);
      setNoticeLangStr(NOTICE_LANGS.find((l) => l.value == res.lang)?.label || "");
    } catch {
      /* keep default */
    }
  }, []);

  React.useEffect(() => {
    if (!isSpikexPc) return;
    loadNoticeLang();
    getUserNoticeConfigList({
      errorPop: false,
      params: { channel: "EMAIL", type: "SPREAD" },
    })
      .then((res) => {
        if (!res?.length) setMarketingOn(true);
        else setMarketingOn(!!res[0].enabled);
      })
      .catch(() => {
        /* keep default */
      });
  }, [isSpikexPc, loadNoticeLang]);

  const lang =
    (typeof window !== "undefined" ? Cookie.get("lang") : null) ??
    appState?.lang ??
    localeToLangKey(routerLocale) ??
    pathnameLang ??
    "en";
  const currency = appState?.currency ?? (typeof window !== "undefined" ? Cookie.get("currency") : null) ?? "usd";

  const currentLangLab = LocalesMap.find((item: { key: string }) => item.key === lang)?.lab ?? lang;

  const handleLang = (key: string) => {
    const obj = LocalesMap.find((item: { key: string }) => item.key === key);
    if (!obj || obj.key === lang) return;
    Cookie.set("lang", obj.key);
    AppCookie.set("langManual", "1");
    setHeader({ lang: obj.key });
    appDispatch?.({ payload: { lang: obj.key } });
    replace("/accounts/user/setting", "/accounts/user/setting", { locale: obj.name });
  };

  const handleCurrency = (key: string) => {
    if (key === currency) return;
    Cookie.set("currency", key);
    appDispatch?.({ payload: { currency: key } });
    store.currency.updateAssist(key);
  };

  const handleMarketing = async (next: boolean) => {
    setMarketingOn(next);
    try {
      await putUserNoticeConfig({
        errorPop: true,
        successPop: true,
        data: { enabled: next, channel: "EMAIL", type: "SPREAD" },
      });
    } catch {
      setMarketingOn(!next);
    }
  };

  const saveNoticeLang = axiosThrottle(async (unlock) => {
    const { err } = await waitRes(put_noticeLang, { data: { lang: noticeLangVal }, errorPop: true });
    unlock();
    if (err) return;
    message.success(t("account.success"));
    setNoticeLangVisible(false);
    loadNoticeLang();
  });

  const mobileItems = [
    { key: "language", label: t("setting.language"), value: currentLangLab, path: "/accounts/user/setting-language" },
    { key: "notification", label: t("setting.notification"), value: "", path: "/accounts/user/notification-setting" },
  ];

  // 仅保留 Notification Settings 原有两项；其余 mock 项暂不可用
  // const noticeRowsUnavailable = [
  //   { key: "trade", title: t("setting.noticeTrade"), desc: t("setting.noticeTradeDesc") },
  //   { key: "ai", title: t("setting.noticeAi"), desc: t("setting.noticeAiDesc") },
  //   { key: "price", title: t("setting.noticePrice"), desc: t("setting.noticePriceDesc") },
  //   { key: "security", title: t("setting.noticeSecurity"), desc: t("setting.noticeSecurityDesc") },
  // ];

  return (
    <div className={cx(styles["settings-page"], isSpikexPc && styles.spikexPc, className)}>
      {/* Mobile: keep drill-down list */}
      <div className={styles["settings-list"]}>
        {mobileItems.map((item) => (
          <div
            key={item.key}
            role="button"
            tabIndex={0}
            className={styles["settings-list-item"]}
            onClick={() => item.path && push(item.path)}
            onKeyDown={(e) => e.key === "Enter" && item.path && push(item.path)}
          >
            <span className={styles["settings-list-label"]}>{item.label}</span>
            {item.value ? <span className={styles["settings-list-value"]}>{item.value}</span> : null}
            <span className={styles["settings-list-chevron"]}>{CHEVRON_RIGHT}</span>
          </div>
        ))}
      </div>

      {/* PC: Display + Notifications (spikex-ai-web preferences) */}
      {isSpikexPc ? (
        <div className={styles.prefs}>
          <section className={styles.prefsCard}>
            <h3 className={styles.prefsCardTitle}>{t("setting.display") || "Display"}</h3>
            <div className={styles.prefsRow}>
              <div className={styles.prefsRowText}>
                <div className={styles.prefsRowTitle}>{t("setting.language")}</div>
                <div className={styles.prefsRowDesc}>
                  {t("setting.languageDescApp") || "Used across the app and emails."}
                </div>
              </div>
              <Segmented
                options={LocalesMap.map((l: { key: string }) => l.key) as string[]}
                value={lang}
                onChange={handleLang}
                labels={Object.fromEntries(LocalesMap.map((l: { key: string; lab: string }) => [l.key, l.lab]))}
              />
            </div>
            <div className={styles.prefsRow}>
              <div className={styles.prefsRowText}>
                <div className={styles.prefsRowTitle}>{t("setting.displayCurrency") || "Display currency"}</div>
                <div className={styles.prefsRowDesc}>
                  {t("setting.currencyDescBalance") || "Estimated balances are shown in this currency."}
                </div>
              </div>
              <Segmented
                options={CURRENCY_OPTS.map((c) => c.key)}
                value={currency}
                onChange={handleCurrency}
                labels={Object.fromEntries(CURRENCY_OPTS.map((c) => [c.key, c.name]))}
              />
            </div>
          </section>

          <section className={cx(styles.prefsCard, styles.prefsCardFlush)}>
            <div className={styles.prefsCardHead}>
              <p className={styles.prefsCardHeadLabel}>{t("notice.setting") || "Notification Settings"}</p>
            </div>
            <div className={styles.prefsNoticeList}>
              <div className={styles.prefsNoticeRow}>
                <div className={styles.prefsRowText}>
                  <div className={styles.prefsRowTitle}>{t("notice.lang")}</div>
                  <div className={styles.prefsRowDesc}>{t("notice.langDec")}</div>
                </div>
                <div className={styles.prefsNoticeAction}>
                  {noticeLangStr ? <span className={styles.prefsNoticeValue}>{noticeLangStr}</span> : null}
                  <button type="button" className={styles.prefsGhostBtn} onClick={() => setNoticeLangVisible(true)}>
                    {t("account.change")}
                  </button>
                </div>
              </div>
              <div className={styles.prefsNoticeRow}>
                <div className={styles.prefsRowText}>
                  <div className={styles.prefsRowTitle}>{t("market.title")}</div>
                  <div className={styles.prefsRowDesc}>{t("market.dec")}</div>
                </div>
                <Toggle on={marketingOn} onChange={handleMarketing} />
              </div>
            </div>
          </section>

          <Modal
            className={cx(modalStyles.spikexModal, styles.noticeLangModal)}
            open={noticeLangVisible}
            title={t("notice.changeLang")}
            width={440}
            footer={null}
            centered
            onCancel={() => setNoticeLangVisible(false)}
            destroyOnClose
          >
            <p className={styles.noticeLangTip}>{t("notice.langDec")}</p>
            <div className={styles.noticeLangLabel}>{t("notice.lang")}</div>
            <Select
              className={styles.noticeLangSelect}
              dropdownClassName={styles.noticeLangSelectDropdown}
              style={{ width: "100%" }}
              value={noticeLangVal}
              onChange={setNoticeLangVal}
              options={NOTICE_LANGS}
            />
            <button type="button" className={styles.noticeLangConfirm} onClick={saveNoticeLang}>
              {t("account.confirm")}
            </button>
          </Modal>
        </div>
      ) : null}
    </div>
  );
};

export default observer(Setting);
